<template>
  <ReportDetailView
    v-if="selectedReport"
    :report="selectedReport"
    @back="handleDetailBack"
    @updated="handleDetailUpdated"
  />

  <div
    v-else
    class="manager-reports-ant"
    :class="{ 'is-loading': loading }"
    :aria-busy="loading ? 'true' : undefined"
  >
    <h2 class="manager-reports-ant__title">Отчеты</h2>

    <div class="manager-reports-ant__action-bar">
      <SearchInput v-model="searchQuery" placeholder="Найти..." @search="handleSearch" />

      <button type="button" class="btn btn-secondary btn-medium" @click="filterOpen = true">
        <FilterIcon :filled="isFilterApplied" />
        <span>Фильтр</span>
      </button>

      <a
        :href="exportUrl"
        class="btn btn-secondary btn-medium manager-reports-ant__download-btn"
        target="_blank"
        rel="noopener"
        title="Скачать все отчёты в Excel"
      >
        Скачать отчеты (.xlsx)
      </a>
    </div>

    <p
      v-if="hasActiveSearch"
      class="search-summary manager-reports-ant__search-summary"
      v-html="searchSummaryMarkup"
    />

    <div class="manager-reports-ant__table-wrapper personal-area-table-wrapper">
      <div v-if="shouldShowEmpty" class="manager-reports-ant__list--empty">
        <div class="manager-reports-ant__list--empty-title">{{ emptyState.title }}</div>
        <div
          v-if="emptyState.description"
          class="manager-reports-ant__list--empty-description"
        >
          {{ emptyState.description }}
        </div>
      </div>

      <a-table
        v-else-if="shouldShowTable"
        class="manager-reports-ant__table"
        :columns="columns"
        :bordered="true"
        :data-source="paginatedItems"
        :loading="loading"
        :pagination="false"
        :scroll="TABLE_SCROLL"
        :custom-row="getRowProps"
        row-key="id"
      >
        <template #bodyCell="{ column, record }">
          <template v-if="column.key === 'residentName'">
            {{ record.residentName }}
          </template>

          <template v-else-if="column.key === 'createdAt'">
            {{ formatManagerReportDate(record.createdAt) }}
          </template>

          <template v-else-if="column.key === 'reportYear'">
            {{ formatReportYear(record.reportYear) }}
          </template>

          <template v-else-if="column.key === 'status'">
            <span
              class="manager-reports-ant__status"
              :class="`manager-reports-ant__status--${getStatusModifier(record.status)}`"
            >
              <span class="manager-reports-ant__status-dot" aria-hidden="true" />
              <span class="manager-reports-ant__status-text">{{ record.status || '—' }}</span>
            </span>
          </template>

          <template v-else-if="column.key === 'signature'">
            <span
              class="manager-reports-ant__signature"
              :class="
                isReportSigned(record)
                  ? 'manager-reports-ant__signature--signed'
                  : 'manager-reports-ant__signature--unsigned'
              "
              @click.stop
            >
              <img
                :src="isReportSigned(record) ? SIGNED_ICON : NOT_SIGNED_ICON"
                alt=""
                aria-hidden="true"
              />
              {{ isReportSigned(record) ? 'Подписан' : 'Не подписан' }}
            </span>
          </template>
        </template>
      </a-table>
    </div>

    <Pagination
      v-if="filteredItems.length > pageSize"
      v-model="currentPage"
      class="manager-reports-ant__pagination personal-area-table-pagination"
      :total-count="filteredItems.length"
      :page-size="pageSize"
      @change="handlePageChange"
    />

    <ReportsFilterDrawer
      v-model:open="filterOpen"
      :clusters="clusters"
      :status-options="statusOptions"
      :year-options="yearOptions"
      :filter="appliedFilter"
      @apply="handleFilterApply"
    />
  </div>
</template>

<script setup lang="ts">
import { computed, onMounted, ref } from 'vue';
import type { TableColumnsType } from 'ant-design-vue';
import FilterIcon from '@/js/components/icons/FilterIcon.vue';
import {
  showStcErrorNotification,
} from '@/js/components/stc-notification';
import { getAssetsPath } from '@/js/utils/getAssetsDir';
import { formatMatchCountSummary } from '@/js/utils/search-highlight';
import SearchInput from '../SearchInput.vue';
import Pagination from '../Pagination.vue';
import ReportDetailView from './ReportDetailView.vue';
import ReportsFilterDrawer from './ReportsFilterDrawer.vue';
import {
  fetchManagerReportsList,
  getManagerReportsExportUrl,
} from './manager-reports-api';
import {
  formatManagerReportDate,
  filterManagerReports,
  getStatusModifier,
} from './map-manager-report';
import {
  MANAGER_REPORTS_PAGE_SIZE,
  MANAGER_REPORT_STATUS_OPTIONS,
  getManagerReportYearOptions,
  type ManagerReportRow,
  type ManagerReportsClusterOption,
  type ManagerReportsFilter,
} from './types';

const SEARCH_MIN_LENGTH = 3;
const TABLE_SCROLL = { x: 1050, y: 500 };
const pageSize = MANAGER_REPORTS_PAGE_SIZE;
const SIGNED_ICON = getAssetsPath('/assets/images/icons/resident-lk/signed.svg');
const NOT_SIGNED_ICON = getAssetsPath('/assets/images/icons/resident-lk/not-signed.svg');

const columns: TableColumnsType<ManagerReportRow> = [
  {
    title: 'Резидент',
    key: 'residentName',
    dataIndex: 'residentName',
    width: 240,
  },
  {
    title: 'Дата подачи',
    key: 'createdAt',
    dataIndex: 'createdAt',
    width: 160,
  },
  {
    title: 'Отчетный период',
    key: 'reportYear',
    dataIndex: 'reportYear',
    width: 160,
  },
  {
    title: 'Статус',
    key: 'status',
    dataIndex: 'status',
    width: 200,
    customCell: () => ({
      class: 'manager-reports-ant__status-cell',
    }),
  },
  {
    title: 'Эл. подпись',
    key: 'signature',
    dataIndex: 'isSign',
    width: 150,
  },
];

const loading = ref(false);
const errorMessage = ref('');
const allItems = ref<ManagerReportRow[]>([]);
const clusters = ref<ManagerReportsClusterOption[]>([]);
const searchQuery = ref('');
const appliedSearchQuery = ref('');
const currentPage = ref(1);
const filterOpen = ref(false);
const appliedFilter = ref<ManagerReportsFilter>({});
const selectedReport = ref<ManagerReportRow | null>(null);

const statusOptions = MANAGER_REPORT_STATUS_OPTIONS.map((option) => ({
  value: option.id,
  label: option.name,
}));

const yearOptions = computed(() => getManagerReportYearOptions());

const isFilterApplied = computed(() =>
  Boolean(appliedFilter.value.clusterId || appliedFilter.value.statusId || appliedFilter.value.year),
);

const hasActiveSearch = computed(() => appliedSearchQuery.value.trim().length >= SEARCH_MIN_LENGTH);

const searchSummaryMarkup = computed(() =>
  formatMatchCountSummary(filteredItems.value.length, appliedSearchQuery.value.trim()),
);

const shouldShowTable = computed(() => loading.value || filteredItems.value.length > 0);

const shouldShowEmpty = computed(
  () => !loading.value && filteredItems.value.length === 0 && !hasActiveSearch.value,
);

const emptyState = computed(() => {
  if (errorMessage.value) {
    return {
      title: errorMessage.value,
      description: '',
    };
  }

  if (isFilterApplied.value) {
    return {
      title: '0 результатов',
      description: 'Измените значения в фильтре.',
    };
  }

  return {
    title: 'Отчётов нет',
    description: '',
  };
});

const exportUrl = computed(() => getManagerReportsExportUrl());

const filteredItems = computed(() =>
  filterManagerReports(allItems.value, {
    q: appliedSearchQuery.value,
    filter: appliedFilter.value,
  }),
);

const paginatedItems = computed(() => {
  const start = (currentPage.value - 1) * pageSize;
  return filteredItems.value.slice(start, start + pageSize);
});

function formatReportYear(year?: string | null): string {
  return year ? `${year} г.` : '—';
}

function isReportSigned(record: ManagerReportRow): boolean {
  return Boolean(record.isSign ?? record.isSigned);
}

async function loadReports() {
  loading.value = true;
  errorMessage.value = '';

  try {
    const data = await fetchManagerReportsList({
      q: appliedSearchQuery.value,
      filter: appliedFilter.value,
    });

    allItems.value = data.items;
    clusters.value = data.clusters;
    currentPage.value = 1;
  } catch (error) {
    allItems.value = [];
    const message =
      error instanceof Error ? error.message : 'Не удалось загрузить список отчётов';
    errorMessage.value = message;
    showStcErrorNotification(message);
  } finally {
    loading.value = false;
  }
}

function handleSearch(query: string) {
  appliedSearchQuery.value = query;
  currentPage.value = 1;
}

function handlePageChange(page: number) {
  currentPage.value = page;
}

function handleFilterApply(filter: ManagerReportsFilter) {
  appliedFilter.value = filter;
  currentPage.value = 1;
  void loadReports();
}

function getRowProps(record: ManagerReportRow) {
  return {
    onClick: () => {
      selectedReport.value = record;
    },
  };
}

function handleDetailBack() {
  selectedReport.value = null;
}

function handleDetailUpdated() {
  void loadReports();
}

onMounted(() => {
  void loadReports();
});
</script>
